/**
 * Putting an event in the visitor's own calendar.
 *
 * - **Google Calendar** takes a link: it opens Google with the event filled in
 *   and the visitor presses Save. Nothing is downloaded.
 * - **Apple Calendar** has no link format — it only adds events from an `.ics`
 *   file. Switched off for now: the browser-built file below is often saved to
 *   Files on an iPhone rather than opened in Calendar. Kept, commented out.
 *
 * Times are the API's clock strings in the MDS timezone (`"18:00"`, `"24:00"`
 * for the end of the day) and are sent with Beirut as their zone, so a visitor
 * abroad still sees the right hour.
 */

const TIMEZONE = 'Asia/Beirut';

export interface CalendarEvent {
  /** Stable for the event, so adding it twice updates rather than duplicates. */
  uid: string;
  title: string;
  /** `YYYY-MM-DD`. */
  date: string;
  startTime: string;
  endTime: string;
  location?: string;
  description?: string;
}

/** `2026-10-06`, `"17:00"` → `20261006T170000`; `"24:00"` rolls over to the next day. */
function stamp(date: string, time: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const at = new Date(Date.UTC(y!, m! - 1, d!, hours!, minutes!));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getUTCFullYear()}${pad(at.getUTCMonth() + 1)}${pad(at.getUTCDate())}T${pad(at.getUTCHours())}${pad(at.getUTCMinutes())}00`;
}

/** The Google Calendar "new event" page, filled in. Open it in a new tab. */
export function googleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${stamp(event.date, event.startTime)}/${stamp(event.date, event.endTime)}`,
    ctz: TIMEZONE,
  });
  if (event.location) params.set('location', event.location);
  if (event.description) params.set('details', event.description);
  return `https://calendar.google.com/calendar/render?${params}`;
}

// Apple Calendar — switched off for now (see `components/action/add-to-calendar.tsx`).
// Kept whole so it can come back as it was.
//
// /** Text as iCalendar wants it: backslashes, commas, semicolons and newlines escaped. */
// const icsText = (value: string) =>
//   value.replace(/\\/g, '\\\\').replace(/[,;]/g, (c) => `\\${c}`).replace(/\r?\n/g, '\\n');
//
// /** Hands the browser a one-event `.ics` file, which Apple Calendar opens. */
// export function downloadAppleCalendarFile(event: CalendarEvent): void {
//   const lines = [
//     'BEGIN:VCALENDAR',
//     'VERSION:2.0',
//     'PRODID:-//MDS//Events//EN',
//     'BEGIN:VEVENT',
//     `UID:${event.uid}`,
//     `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').slice(0, 15)}Z`,
//     `DTSTART;TZID=${TIMEZONE}:${stamp(event.date, event.startTime)}`,
//     `DTEND;TZID=${TIMEZONE}:${stamp(event.date, event.endTime)}`,
//     `SUMMARY:${icsText(event.title)}`,
//     ...(event.location ? [`LOCATION:${icsText(event.location)}`] : []),
//     ...(event.description ? [`DESCRIPTION:${icsText(event.description)}`] : []),
//     'END:VEVENT',
//     'END:VCALENDAR',
//   ];
//
//   const url = URL.createObjectURL(
//     new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' }),
//   );
//   const link = document.createElement('a');
//   link.href = url;
//   link.download = `${event.title.replace(/[^\w-]+/g, '-').toLowerCase()}.ics`;
//   link.click();
//   URL.revokeObjectURL(url);
// }
